/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { memo, useState, useEffect, useMemo, useRef, useCallback } from "react";

// antd:
import { Select, Skeleton } from "antd";

// import type * as CSS from "csstype";

interface Option {
  value:string;
  label?:string;
  title?:string;
  disabled?:boolean;
  options?:Option
}

// Redux-hooks:
// import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";

// Reducers:
// import { getDict, getTheme, getLng } from "../../libs/reducer.ts";

const { rz } = window;

type componentProps = {
  params:{
    value:string | string[] | {[key:string]:any}[] | undefined,
    values?:(itms:string[] | {[key:string]:any}[] | undefined) => Promise<Array<Option>>,
    onValueChange:(newValue:string | string[] | {[key:string]:any}[] | undefined) => void,
    specialCellEditorOnChange?:(newValue:string | string[] | {[key:string]:any}[] | undefined) => void,
    stopEditing?:() => void, saveToDatabase?:boolean, node:any,
    column:{[key:string]:any},
    appkey?:string,
    gridRef?:any,
    aggridkey?:string,
    fullPath?:string,
    lng?:string,
    user?:{login:string, groups:string[], name?:string},
    lookupField?:string,
    eventKey?:string,
    rowIndex?:number | string,
    items?:Option[],
    options?:Option[],
    allowClear?:boolean,
    virtual?:boolean,
    defaultOpen?:boolean,
    autoFocus?:boolean,
    placeholder?:string,
    mode?:"multiple" | "tags" | "single",
    multiple?:boolean,
    tooltip?:string,
    showSearch?:boolean
    safe?:boolean
  }
} & {[key:string]:any}; // string | boolean | number | React.ReactNode

const getAllItems = ({appkey, node, column, lng, user, items, lookupField, safe}) => {
  if (!items || rz.isPromise(items)) { return [] }
  if (rz.isFunction(items)) { return items({appkey, node, column, lng, user, lookupField}) || [] }
  if (rz.isString(items)) {
    if (appkey && window[appkey] && window[appkey][items] && rz.isPromise(window[appkey][items])) { return [] }
    if (appkey && window[appkey] && window[appkey][items] && rz.isFunction(window[appkey][items])) { return window[appkey][items]({appkey, node, column, lng, user, lookupField, safe}) || [] }
  }
  if (rz.isArray(items)) {
    return items.map((vv) => {
      if (rz.isString(vv)) { return {_id: vv, value: vv, title: vv, label: vv} }
      if (rz.isObject(vv)) {
        const ttl = rz.strFromSelectedLng({lng: lng || "en", ru: vv.title_ru || vv.label_ru, en: vv.title_en || vv.label_en, tr: vv.title_tr || vv.label_tr, def: vv.title || vv.label || vv[lookupField] || vv.value || vv._id || ""});
        return {_id: vv._id, value: vv[lookupField] || vv.value || vv._id, title: ttl, label: ttl}
      }
      return null;
    }).filter((vv) => (vv && vv.value));
  }
  return [];
};

const getSelectedValues = (value, allItems) => {
  let retVal = value;
  if (allItems && allItems[0] && rz.isObject(allItems[0])) {
    // Восстановим настоящий value, если неверно передано первоначальное значение ячейки AGgrid (например когда использовали функцию valueGetter)
    if (value && rz.isString(value)) {
      const retObj = allItems.find((ii) => ((ii.value && ii.value === value) || (ii._id && ii._id === value) || (ii.title && ii.title === value) || (ii.label && ii.label === value)));
      if (retObj) { retVal = retObj.value || retObj._id }
    }
    else if (rz.isArray(value) && rz.isString(value[0])) {
      const retObjs = allItems.filter((ii) => (rz.arrContains(value, ii.value) || rz.arrContains(value, ii._id) || rz.arrContains(value, ii.title) || rz.arrContains(value, ii.label)));
      if (retObjs.length) { retVal = retObjs.map((ii) => (ii.value || ii._id)) }
    }
    else if (rz.isObject(value) && (value.value || value._id)) {
      const retObj = allItems.find((ii) => ((value.value && ii.value === value.value) || (value._id && ii._id === value._id) || (value.title && ii.title === value.title) || (value.label && ii.label === value.label)));
      if (retObj) { retVal = retObj.value || retObj._id }
    }
    else if (rz.isArray(value) && rz.isObject(value[0])) {
      const vValue = value.map((ii) => (ii && ii.value)).filter((ii) => (ii));
      const v_id = value.map((ii) => (ii && ii._id)).filter((ii) => (ii));
      const vTitle = value.map((ii) => (ii && ii.title)).filter((ii) => (ii));
      const vLabel = value.map((ii) => (ii && ii.label)).filter((ii) => (ii));
      const retObjs = allItems.filter((ii) => ((ii.value && rz.arrContains(vValue, ii.value)) || (ii._id && rz.arrContains(v_id, ii._id)) || (ii.title && rz.arrContains(vTitle, ii.title)) || (ii.label && rz.arrContains(vLabel, ii.label))));
      if (retObjs.length) { retVal = retObjs.map((ii) => (ii.value || ii._id)) }
    }
  }
  return retVal;
};

// eslint-disable-next-line react/display-name
export default memo((params:componentProps) => {
  const {
    appkey, aggridkey, gridRef, fullPath, lng, user, lookupField, value, values, onValueChange, eventKey,
    rowIndex, column, items, mode, multiple, allowClear, virtual, defaultOpen, autoFocus, tooltip, placeholder, showSearch,
    specialCellEditorOnChange, stopEditing, node, saveToDatabase, safe
  } = params;
  // Debug:
  // console.log({ value, items, values });

  const refSelect = useRef(null);

  const [loading, setLoading] = useState(true);
  const [selectItems, setItems] = useState(getAllItems({appkey, node, column, lng, user, items: items || values, lookupField, safe}));

  const selectType = useMemo(() => (multiple === true || mode === "multiple" ? "multiple" : mode), [mode, multiple]);

  useEffect(() => {
    if (appkey && rz.isString(values) && window[appkey] && window[appkey][values]) {
      const userFnc = window[appkey][values];
      if (rz.isPromise(userFnc)) {
        userFnc({appkey, node, column, lng, user, lookupField, safe}).then((rowsAns) => {
          if (rowsAns && rz.isArray(rowsAns)) {
            const rws = rowsAns.map((rr) => (rz.isObject(rr) ? {value: rr.value || rr._id, label: rr.label || rr.title || rr.value || rr._id} : {value: rr, label: rr}));
            setItems(rws);
          }
          setLoading(false);
        }).catch((err) => { console.log(err) });
      }
      else if (rz.isFunction(userFnc)) {
        const rowsAns = userFnc({appkey, node, column, lng, user, lookupField, safe});
        if (rowsAns && rz.isArray(rowsAns)) {
          const rws = rowsAns.map((rr) => (rz.isObject(rr) ? {value: rr.value || rr._id, label: rr.label || rr.title || rr.value || rr._id} : {value: rr, label: rr}));
          setItems(rws);
        }
        setLoading(false);
      }
    }
    else if (rz.isPromise(values)) {
      values({appkey, node, column, lng, user, lookupField, safe}).then((rowsAns) => {
        if (rowsAns && rz.isArray(rowsAns)) {
          const rws = rowsAns.map((rr) => (rz.isObject(rr) ? {value: rr.value || rr._id, label: rr.label || rr.title || rr.value || rr._id} : {value: rr, label: rr}));
          setItems(rws);
        }
        setLoading(false);
      }).catch((err) => { console.log(err) });
    }
    else if (rz.isFunction(values)) {
      const rowsAns = values({appkey, node, column, lng, user, lookupField, safe});
      if (rowsAns && rz.isArray(rowsAns)) {
        const rws = rowsAns.map((rr) => (rz.isObject(rr) ? {value: rr.value || rr._id, label: rr.label || rr.title || rr.value || rr._id} : {value: rr, label: rr}));
        setItems(rws);
      }
      setLoading(false);
    }
    else {
      setLoading(false);
    }
  }, [aggridkey, appkey, column, fullPath, items, lng, lookupField, node, rowIndex, user, values, safe]);

  const [myValues, setMyValues] = useState(getSelectedValues(value, selectItems));

  useEffect(() => {
    setMyValues(getSelectedValues(value, selectItems));
  }, [value, selectItems]);

  const onChange = useCallback((val: Option | Option[], option: Option | Option[]) => {
    // console.log({val, option, column, rowIndex, selectItems});
    const retVal = getSelectedValues(val, selectItems);
    setMyValues(retVal);
    if (specialCellEditorOnChange) { specialCellEditorOnChange({appkey, aggridkey, gridRef, fullPath, controlType: "select", lookupField, value: retVal, items: selectItems, column, rowIndex, lng, user, option, node, saveToDatabase, safe}) }
    else { onValueChange(retVal) }
    if (selectType !== "multiple") { stopEditing() }
  }, [aggridkey, gridRef, appkey, column, fullPath, lng, user, lookupField, onValueChange, rowIndex, selectItems, specialCellEditorOnChange, node, saveToDatabase, selectType, stopEditing, safe]);

  useEffect(() => {
    // console.log({ myValues, selectItems });
    // if (refSelect?.current) { refSelect.current.focus() }
  }, [myValues, selectItems]);

  const filterOption = useCallback((inputValue:string, option:any) => {
    let item = (rz.isObject(option) ? (option.title || option.label || option.value || option.key) : (option || ""));
    if (rz.isObject(item) && item.props && item.props.children && item.props.children.length) { // If we have a ReactElement inside 'option'
      const chlds = item.props.children.filter((ch) => (ch && ch.props && ch.props.children && rz.isString(ch.props.children))).map((ch) => (ch.props.children));
      if (chlds && chlds.length) { item = chlds[0] }
    }
    return rz.strCompare(item, inputValue, ".caseNoSensitive.toLowerCase.trim.substring");
  }, []);

  return (
    loading
      ? (<Skeleton loading={loading} active />)
      : (<Select
          options={selectItems}
          value={myValues}
          // defaultValue={myValues}
          // initialValue={myValues}
          onChange={onChange}
          filterOption={filterOption}
          mode={selectType}
          allowClear={rz.isUndefinedOrNull(allowClear) ? true : allowClear}
          virtual={rz.isUndefinedOrNull(virtual) ? false : virtual}
          defaultOpen={rz.isUndefinedOrNull(defaultOpen) ? true : defaultOpen}
          autoFocus={rz.isUndefinedOrNull(autoFocus) ? true : autoFocus}
          showSearch={rz.isUndefinedOrNull(showSearch) ? true : showSearch}
          variant={"outlined"}
          maxTagCount={100}
          ref={refSelect}
          className={"rz-width-1-1"}
          placeholder={placeholder}
          uk-tooltip={tooltip}
          style={{minWidth: 200}}
      />)
  );
});
/* eslint-enable @typescript-eslint/no-unused-vars */
